/**
 * Le `detail` d'une erreur FastAPI est une chaîne… sauf sur un 422, où
 * Pydantic renvoie une LISTE d'objets `{loc, msg, type}`. Une centaine
 * d'écrans de cloud-admin affichent `error.response.data.detail` tel quel :
 * rendu comme enfant React, un tableau d'objets fait tomber la page entière
 * sur l'ErrorBoundary (constat 2026-09-29 : un email refusé par `EmailStr`
 * sur staging → « Quelque chose s'est mal passé »).
 *
 * L'intercepteur du client HTTP passe chaque `detail` par ici : côté écrans,
 * `detail` est TOUJOURS une chaîne lisible.
 */

interface ErreurPydantic {
  loc?: Array<string | number>;
  msg?: string;
  type?: string;
}

/** Nomme le champ sans le préfixe technique (`body`, `query`, `path`). */
function nomDuChamp(loc: Array<string | number> | undefined): string {
  if (!loc || loc.length === 0) return '';
  const utiles = loc.filter((seg) => !['body', 'query', 'path', 'header'].includes(String(seg)));
  return utiles.map(String).join('.');
}

export function aplatirDetail(detail: unknown): string | undefined {
  if (detail === undefined || detail === null) return undefined;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const lignes = (detail as ErreurPydantic[]).map((e) => {
      if (!e || typeof e !== 'object') return String(e);
      const champ = nomDuChamp(e.loc);
      const message = e.msg || e.type || JSON.stringify(e);
      return champ ? `${champ} : ${message}` : message;
    });
    return lignes.join(' · ');
  }
  if (typeof detail === 'object') {
    const obj = detail as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    return JSON.stringify(detail);
  }
  return String(detail);
}

/**
 * Réécrit `error.response.data.detail` en chaîne, en place, pour que tout
 * lecteur en aval (`error.response?.data?.detail || …`) reçoive du texte.
 */
export function normaliserErreurApi<T extends { response?: { data?: any } }>(error: T): T {
  const data = error?.response?.data;
  if (data && typeof data === 'object' && 'detail' in data) {
    // Un `detail` objet porte parfois des données utiles à l'écran (refus de
    // licence : coût, solde). On le garde à côté, sous `detailObjet`.
    if (data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)) {
      data.detailObjet = data.detail;
    }
    const texte = aplatirDetail(data.detail);
    if (texte !== undefined) data.detail = texte;
  }
  return error;
}
